import React, { FC, useEffect, useState } from 'react';
import {
  Modal,
  Input,
  InputNumber,
  InputAmount,
  DatePicker,
  Button,
  notification,
} from '@gamesb42/ui-kit';
import moment from 'moment';

import Flex from 'components/Flex';
import baseRequest from 'queries/baseRequest';
import endpoints from 'consts/endpoints';
import { activationOrderDataType } from 'hooks/api/useActivationOrder';

export type DistributionPlatformT = {
  platform_id: string;
  platform: string;
  purchase_percentage: string;
  purchase_price_percentage: string;
  funding_limit: string;
  currency: string;
  platform_logo?: string;
  activation_date?: string;
  status?: string;
};

type PropsT = {
  activationOrderData: activationOrderDataType;
  platform: DistributionPlatformT | null;
  onClose: () => void;
  onSaved: () => void;
};

const buildPlatformsBody = (
  platforms: DistributionPlatformT[],
  editedId: string,
  patch?: Partial<DistributionPlatformT>,
) =>
  platforms
    .filter((p) => (patch ? true : p.platform_id !== editedId))
    .map((p) => (p.platform_id === editedId && patch ? { ...p, ...patch } : p));

const EditRevenueSourceModal: FC<PropsT> = ({ activationOrderData, platform, onClose, onSaved }) => {
  const [purchasePercentage, setPurchasePercentage] = useState<string>('');
  const [purchasePricePercentage, setPurchasePricePercentage] = useState<string>('');
  const [fundingLimit, setFundingLimit] = useState<string>('');
  const [activationDate, setActivationDate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (platform) {
      setPurchasePercentage(platform.purchase_percentage ?? '');
      setPurchasePricePercentage(platform.purchase_price_percentage ?? '');
      setFundingLimit(platform.funding_limit ?? '');
      setActivationDate(platform.activation_date ?? null);
    }
  }, [platform]);

  if (!platform) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const raw = activationOrderData as any;
  const originalPlatforms: DistributionPlatformT[] = raw.distribution_platforms || [];

  const putActivationOrder = (platforms: DistributionPlatformT[]) => {
    const body = {
      company_id: raw.company_id,
      contract_id: raw.contract_id,
      capitalization: raw.capitalization,
      amount: raw.amount,
      commission: raw.commission,
      purchase_date: raw.purchase_date,
      due_date: raw.due_date,
      name: raw.name,
      currency: raw.currency,
      purpose: raw.purpose,
      registration_number: raw.registration_number,
      vat_policy: raw.vat_policy,
      activation_date: raw.activation_date,
      mfa_usd: raw.mfa_usd,
      mfa_eur: raw.mfa_eur,
      distribution_platforms: platforms,
    };

    return baseRequest({
      path: endpoints.getEditAvailableReceivablesUrl(raw.id),
      options: {
        method: 'PUT',
        body: JSON.stringify(body),
      },
    });
  };

  const handleSave = () => {
    setLoading(true);
    const platforms = buildPlatformsBody(originalPlatforms, platform.platform_id, {
      purchase_percentage: String(purchasePercentage ?? ''),
      purchase_price_percentage: String(purchasePricePercentage ?? ''),
      funding_limit: String(fundingLimit ?? ''),
      activation_date: activationDate ? moment(activationDate).format('YYYY-MM-DD') : undefined,
    });

    putActivationOrder(platforms)
      .then(() => {
        notification.success({
          message: `Revenue source «${platform.platform}» updated successfully`,
          theme: 'dark',
          placement: 'bottomLeft',
        });
        onSaved();
        onClose();
      })
      .catch((error) => {
        // eslint-disable-next-line no-console
        console.error('Ошибка при сохранении Revenue Source:', error);
        notification.error({
          message: 'Failed to update revenue source',
          theme: 'dark',
          placement: 'bottomLeft',
        });
      })
      .finally(() => setLoading(false));
  };

  const handleDelete = () => {
    Modal.warning({
      title: 'Delete revenue source',
      content: `Are you sure you want to delete «${platform.platform}»?`,
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: { style: { background: '#EC3E72', border: 'none' } },
      onOk: () => {
        const platforms = buildPlatformsBody(originalPlatforms, platform.platform_id);

        return putActivationOrder(platforms)
          .then(() => {
            notification.success({
              message: `Revenue source «${platform.platform}» deleted successfully`,
              theme: 'dark',
              placement: 'bottomLeft',
            });
            onSaved();
            onClose();
          })
          .catch((error) => {
            // eslint-disable-next-line no-console
            console.error('Ошибка при удалении Revenue Source:', error);
            notification.error({
              message: 'Failed to delete revenue source',
              theme: 'dark',
              placement: 'bottomLeft',
            });
          });
      },
      onCancel: () => {},
    });
  };

  return (
    <Modal
      open
      title="Edit Revenue Source"
      width="766px"
      onCancel={onClose}
      okButtonProps={{ style: { display: 'none' } }}
      footer={
        <Flex justify="flex-end" gap="24px">
          <Button style={{ background: '#EC3E72', color: '#fff', border: 'none' }} onClick={handleDelete}>
            Delete
          </Button>
          <Button type="primary" loading={loading} onClick={handleSave}>
            Save changes
          </Button>
          <Button onClick={onClose}>Cancel</Button>
        </Flex>
      }
    >
      <Flex direction="column" gap="32px">
        <Flex gap="32px">
          <div style={{ flex: 1 }}>
            <Input
              label="Label"
              size="large"
              value={platform.platform}
              disabled
              style={{ width: '100%' }}
              prefix={
                platform.platform_logo ? (
                  <img
                    src={platform.platform_logo}
                    alt={platform.platform}
                    style={{ width: 16, height: 16, marginRight: 4 }}
                  />
                ) : undefined
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <Input
              label="Status"
              size="large"
              value={platform.status ?? 'Active'}
              disabled
              style={{ width: '100%' }}
            />
          </div>
        </Flex>
        <Flex gap="32px">
          <div style={{ flex: 1 }}>
            <InputNumber
              label="Purchase %*"
              size="large"
              value={purchasePercentage}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(e: any) => setPurchasePercentage(e ?? '')}
              style={{ width: '100%' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <InputNumber
              label="Purchase Price %*"
              size="large"
              value={purchasePricePercentage}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              onChange={(e: any) => setPurchasePricePercentage(e ?? '')}
              style={{ width: '100%' }}
            />
          </div>
        </Flex>
        <Flex gap="32px">
          <Input
            label="Currency"
            size="large"
            value={platform.currency?.toUpperCase()}
            disabled
            style={{ width: '218px' }}
          />
          <InputAmount
            label="Funding limit*"
            size="large"
            currency={platform.currency?.toUpperCase() as 'EUR' | 'USD'}
            value={fundingLimit}
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            onChange={(e: any) => setFundingLimit(e ?? '')}
            style={{ width: '218px' }}
          />
          <DatePicker
            label="Activation date*"
            size="large"
            format="DD.MM.YYYY"
            value={activationDate ? moment(activationDate) : null}
            onChange={(e) => setActivationDate(e ? moment(e).format('YYYY-MM-DD') : null)}
            style={{ width: '218px' }}
          />
        </Flex>
      </Flex>
    </Modal>
  );
};

export default EditRevenueSourceModal;
